import { api, getData, postData, type Envelope } from "@/services/api";

export interface MeUser {
  id: string;
  org_id: string;
  org_slug: string;
  org_name: string;
  email: string;
  name: string;
  locale: string;
  totp_enabled: boolean;
  status: string;
  presence_status?: string;
}

export interface LoginResponse {
  access_token: string;
  access_expires_at: string;
  token_type: string;
  user: MeUser;
  permissions: string[];
}

export interface MeBundle {
  user: MeUser;
  permissions: string[];
}

export interface SessionDTO {
  id: string;
  user_agent: string;
  ip_address: string;
  issued_at: string;
  last_used_at: string;
  expires_at: string;
  is_current: boolean;
  prefix: string;
}

export interface TOTPStart {
  secret: string;
  otpauth_url: string;
  qr_data_url: string;
}

export const authService = {
  async login(email: string, password: string, opts: { orgSlug?: string; totp?: string } = {}): Promise<LoginResponse> {
    return postData<LoginResponse>("/v1/auth/login", {
      email,
      password,
      org_slug: opts.orgSlug || undefined,
      totp_code: opts.totp || undefined,
    });
  },

  async logout(): Promise<void> {
    await api.post("/v1/auth/logout", {}, { withCredentials: true });
  },

  async me(): Promise<MeBundle> {
    return getData<MeBundle>("/v1/me");
  },

  async listSessions(): Promise<SessionDTO[]> {
    return getData<SessionDTO[]>("/v1/me/sessions");
  },

  async revokeSession(id: string): Promise<void> {
    await api.delete<Envelope<void>>(`/v1/me/sessions/${id}`);
  },

  async startTOTP(): Promise<TOTPStart> {
    return postData<TOTPStart>("/v1/me/totp/start");
  },

  async confirmTOTP(code: string): Promise<void> {
    await api.post<Envelope<void>>("/v1/me/totp/confirm", { code });
  },

  async disableTOTP(): Promise<void> {
    await api.delete<Envelope<void>>("/v1/me/totp");
  },

  async requestPasswordReset(email: string, orgSlug?: string): Promise<void> {
    await api.post<Envelope<void>>("/v1/auth/password-reset/request", {
      email,
      org_slug: orgSlug || undefined,
    });
  },

  async confirmPasswordReset(token: string, newPassword: string): Promise<void> {
    await api.post<Envelope<void>>("/v1/auth/password-reset/confirm", {
      token,
      new_password: newPassword,
    });
  },

  async acceptInvitation(token: string, name: string, password: string, locale = "pt-BR"): Promise<MeUser> {
    return postData<MeUser>(`/v1/auth/invitations/${encodeURIComponent(token)}/accept`, {
      name,
      password,
      locale,
    });
  },
};
